import { requireService } from '@profgeosoft/di-ez';
import { InfoRoundLight } from '@repo/go-icons';
import {
  TableColoredCell,
  TableInput,
  TableNumberInput,
  TableStringList,
  TableYearPicker,
  TableDatePicker,
  GRID_ROW_NUMBER_COL_DEF,
} from '@repo/go-table';
import { Tooltip } from '@repo/go-ui';
import { format, fromUnixTime, getUnixTime, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { Translation } from 'react-i18next';

import { BitmapImageComponent } from 'src/components/controls/bitmap-image-component/bitmap-image-component';
import { ColorPickerComponent } from 'src/components/controls/color-picker-component';
import { hasValue } from 'src/packages/utils/has-value';
import { MainDirectoryService } from 'src/services/directory-service/main-directory-service';

import type { DirectoryTableMediator } from '../directory-table-mediator';
import type { GridColDef, GridRenderCellParams, GridRenderEditCellParams } from '@repo/go-table';
import type { ControlRulesService } from 'src/services/control-rules-service/control-rules-service';
import type { TRestrictionsRule } from 'src/services/control-rules-service/mappers';
import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type { TControlView } from 'src/services/directory-service/types';
import type { TableSettingsManager } from 'src/services/table-settings-service/table-settings-manager';
import type { UnitsSettingsStore } from 'src/services/units-settings-service';

import { Cell } from '../components/cell/cell';
import { EditComboboxCell } from '../components/edit-combobox-cell';
import { RecordButtons } from '../components/records-button';
import { StatusSwitcher } from '../components/status-switcher';
import { TableCheckbox } from '../components/table-checkbox';
import { TableHeader } from '../components/table-header';
import { 
  ACTIVITY_COLUMN_FIELD,
  RECORD_BUTTONS_COLUMN,
  ROW_NUMBER_COLUMN_BASE_WIDTH,
  ROW_NUMBER_EXTRA_DIGIT_WIDTH,
} from '../consts';
import styles from '../directory-table.module.scss';

const getRowNumberColumnWidth = (rowCount: number): number => {
  const digits = String(Math.max(rowCount, 1)).length;

  return ROW_NUMBER_COLUMN_BASE_WIDTH + Math.max(0, digits - 2) * ROW_NUMBER_EXTRA_DIGIT_WIDTH;
};

const baseColumn: Partial<GridColDef> = {
  minWidth: 30,
  headerAlign: 'left',
  align: 'left',
};

const COLORED_TABLE_CELL_RULES = [
  {
    color: 'rgba(235, 87, 87, 1)',
    backgroundColor: 'rgba(235, 87, 87, 0.2)',
    rule: (value: number) => value < 1,
  },
  {
    color: 'rgba(242, 153, 74, 1)',
    backgroundColor: 'rgba(242, 153, 74, 0.2)',
    rule: (value: number) => value >= 1 && value <= 99,
  },
  {
    color: 'rgba(33, 150, 83, 1)',
    backgroundColor: 'rgba(33, 150, 83, 0.2)',
    rule: (value: number) => value > 99,
  },
];

interface IDirectoryService {
  recordControlsViews: TControlView[];
  unitsSettingsStore?: UnitsSettingsStore;
  controlRulesService: ControlRulesService;
  directoryViewMediator: DirectoryTableMediator | null;
  recordsMap: Map<string | number, DirectoryRecord>;
  records: DirectoryRecord[];
  optionsService: OptionsService;
  tableSettingsManager: TableSettingsManager;
  onControlBlur(record: DirectoryRecord, fieldId: string): void;
  getRecordById(id: number | string): DirectoryRecord | null;
  deleteRecord(record: DirectoryRecord): void;
  resetRecord(record: DirectoryRecord): void;
}

export const useTableColumns = (directory: IDirectoryService): GridColDef[] => {
  const directoryPage = requireService('directoryPage');
  const { createNewRelatedRecord } = requireService('directoryPage');
  const controlViews = directory.recordControlsViews;
  const rowNumberWidth = getRowNumberColumnWidth(directory.records.length);

  return useMemo(() => {
    const renderColumnHeader = (fieldId: string) => (
      <TableHeader
        fieldId={fieldId}
        textTKey={`labels:${fieldId}.label`}
        unitsSettingsStore={directory.unitsSettingsStore}
        settingsManager={directory.tableSettingsManager}
      />
    );

    const columns: GridColDef[] = [
      {
        ...GRID_ROW_NUMBER_COL_DEF,
        disableReorder: true,
        disableColumnMenu: true,
        resizable: true,
        sortable: false,
        width: rowNumberWidth,
        minWidth: rowNumberWidth,
        maxWidth: rowNumberWidth,
        renderCell: (params) => GRID_ROW_NUMBER_COL_DEF.renderCell?.(params),
      },
      {
        ...baseColumn,
        field: RECORD_BUTTONS_COLUMN,
        width: 45,
        minWidth: 45,
        maxWidth: 45,
        sortable: false,
        disableReorder: true,
        disableColumnMenu: true,
        resizable: false,
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<DirectoryRecord>) => (
          <Cell>{<RecordButtons params={params} directory={directory} />}</Cell>
        ),
        renderHeader: () => null,
      },
    ];

    if (directory instanceof MainDirectoryService) {
      columns.push({
        ...baseColumn,
        field: ACTIVITY_COLUMN_FIELD,
        width: 45,
        minWidth: 45,
        maxWidth: 45,
        sortable: false,
        disableReorder: true,
        disableColumnMenu: true,
        resizable: false,
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<DirectoryRecord>) => (
          <Cell>{<StatusSwitcher params={params} directory={directory} />}</Cell>
        ),
        renderHeader: () => (
          <Tooltip title="Some info" placement="top">
            <InfoRoundLight className={styles.infoIcon} />
          </Tooltip>
        ),
      });
    }

    for (const columnView of controlViews) {
      if ('innerColumns' in columnView || columnView.hiddenField) {
        continue;
      }

      switch (columnView.control) {
        case 'Progress': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'number',
            editable: false,
            width: 90,
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              return record.data[columnView.fieldId];
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              return <TableColoredCell value={params.value} isPercent rules={COLORED_TABLE_CELL_RULES} />;
            },
          });

          break;
        }

        case 'CheckBox': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'boolean',
            editable: false,
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              return !!record.data[columnView.fieldId];
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              return <TableCheckbox params={params} columnView={columnView} />;
            },
          });

          break;
        }

        case 'Field': {
          const restrictions = directory.controlRulesService.rules
            .get(columnView.fieldId)
            ?.find((rule) => rule.type === 'restrictions') as TRestrictionsRule | undefined;

          if (columnView.type === 'String') {
            columns.push({
              ...baseColumn,
              field: columnView.fieldId,
              type: 'string',
              editable: directoryPage.isEditing,
              valueGetter: ($, row, _, api) => {
                const id = api.current.getRowId(row);
                const record = directory.getRecordById(id);

                if (!record) {
                  console.error('cannot find corresponding record');
                  return null;
                }

                return record.data[columnView.fieldId] ?? null;
              },
              valueFormatter(value) {
                if (!hasValue(value) || value === '') {
                  return '-';
                }

                return value;
              },
              renderHeader: () => renderColumnHeader(columnView.fieldId),
              renderCell: (params) => {
                const errors =
                  directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

                return (
                  <Cell message={errors}>
                    <p className={styles.textEllipsis}>{params.formattedValue ?? params.value}</p>
                  </Cell>
                );
              },
              renderEditCell: (params: GridRenderEditCellParams<DirectoryRecord>) => {
                const errors =
                  directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

                return (
                  <Cell message={errors} style={{ padding: 0 }}>
                    <TableInput
                      cellParams={params}
                      maxLength={restrictions?.ruleView.maxLength}
                      onChange={(e) =>
                        directory.directoryViewMediator?.onControlValueChange(
                          params.row.recordId,
                          columnView.fieldId,
                          e.target.value,
                        )
                      }
                      onBlur={() => {
                        const record = directory.getRecordById(params.row.recordId);

                        if (!record) {
                          console.error('cannot find corresponding record');
                          return;
                        }

                        directory.onControlBlur(record, columnView.fieldId);
                      }}
                    />
                  </Cell>
                );
              },
            });
          }

          if (columnView.type === 'Number' || columnView.type === 'Integer') {
            columns.push({
              ...baseColumn,
              field: columnView.fieldId,
              type: 'number',
              editable: directoryPage.isEditing,
              valueGetter: ($, row, _, api) => {
                const id = api.current.getRowId(row);
                const record = directory.getRecordById(id);

                if (!record) {
                  console.error('cannot find corresponding record');
                  return null;
                }

                return record.data[columnView.fieldId] ?? null;
              },
              valueFormatter(value) {
                if (!hasValue(value) || value === '') {
                  return '-';
                }

                return value;
              },
              renderHeader: () => renderColumnHeader(columnView.fieldId),
              renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
                const errors =
                  directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

                return (
                  <Cell message={errors}>
                    <p className={styles.textEllipsis}>{params.formattedValue ?? params.value}</p>
                  </Cell>
                );
              },
              renderEditCell: (params: GridRenderEditCellParams<DirectoryRecord>) => {
                const errors =
                  directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

                return (
                  <Translation>
                    {(t) => (
                      <Cell message={errors} style={{ padding: 0 }}>
                        <TableNumberInput
                          cellParams={params}
                          value={params.value ?? null}
                          placeholder={t('common:placeholders.enterValue')}
                          onChange={(value) =>
                            directory.directoryViewMediator?.onControlValueChange(
                              params.row.recordId,
                              columnView.fieldId,
                              value,
                            )
                          }
                          onBlur={() => {
                            const record = directory.getRecordById(params.row.recordId);

                            if (!record) {
                              console.error('cannot find corresponding record');
                              return;
                            }

                            directory.onControlBlur(record, columnView.fieldId);
                          }}
                          allowClear
                        />
                      </Cell>
                    )}
                  </Translation>
                );
              },
            });
          }

          break;
        }

        case 'NumbersList': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'string',
            editable: directoryPage.isEditing,
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              return record.data[columnView.fieldId] ?? null;
            },
            valueFormatter(value: unknown) {
              if (!hasValue(value) || !Array.isArray(value) || !value.length) {
                return '-';
              }

              return value.join(', ');
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors}>
                  <p className={styles.textEllipsis}>{params.formattedValue ?? params.value}</p>
                </Cell>
              );
            },
            renderEditCell: (params) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors} style={{ padding: 0 }}>
                  <TableStringList
                    cellParams={params}
                    value={params.value}
                    autoFocus={params.hasFocus}
                    type="number"
                    onChange={(value) =>
                      directory.directoryViewMediator?.onControlValueChange(
                        params.row.recordId,
                        columnView.fieldId,
                        value,
                      )
                    }
                  />
                </Cell>
              );
            },
          });

          break;
        }

        case 'StringsList': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'string',
            editable: directoryPage.isEditing,
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              return record.data[columnView.fieldId] ?? null;
            },
            valueFormatter(value: unknown) {
              if (!hasValue(value) || !Array.isArray(value) || !value.length) {
                return '-';
              }

              return value.join(', ');
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors}>
                  <p className={styles.textEllipsis}>{params.formattedValue ?? params.value}</p>
                </Cell>
              );
            },
            renderEditCell: (params) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors} style={{ padding: 0 }}>
                  <TableStringList
                    cellParams={params}
                    value={params.value}
                    autoFocus={params.hasFocus}
                    type="string"
                    onChange={(value) =>
                      directory.directoryViewMediator?.onControlValueChange(
                        params.row.recordId,
                        columnView.fieldId,
                        value,
                      )
                    }
                  />
                </Cell>
              );
            },
          });

          break;
        }

        case 'YearOnlyPicker': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'date',
            editable: directoryPage.isEditing,
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const value = record.data[columnView.fieldId] ?? null;

              if (!value || typeof value !== 'number') {
                return null;
              }

              return fromUnixTime(value);
            },
            valueFormatter: (value) => {
              if (!value) {
                return '-';
              }

              return format(value, 'yyyy');
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors}>
                  <p className={styles.textEllipsis}>{params.formattedValue}</p>
                </Cell>
              );
            },
            renderEditCell: (params: GridRenderEditCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors} style={{ padding: 0 }}>
                  <TableYearPicker
                    cellParams={params}
                    value={params.value}
                    onChange={(value) =>
                      directory.directoryViewMediator?.onControlValueChange(
                        params.row.recordId,
                        columnView.fieldId,
                        value,
                      )
                    }
                    onBlur={() => {
                      const record = directory.getRecordById(params.row.recordId);

                      if (!record) {
                        console.error('cannot find corresponding record');
                        return;
                      }

                      directory.onControlBlur(record, columnView.fieldId);
                    }}
                  />
                </Cell>
              );
            },
          });

          break;
        }

        case 'DateOnlyPicker': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'date',
            editable: directoryPage.isEditing,
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const value = record.data[columnView.fieldId] ?? null;

              if (!value || typeof value !== 'number') {
                return null;
              }

              return fromUnixTime(value);
            },
            valueFormatter: (value) => {
              if (!value) {
                return '-';
              }

              const dateFormat = columnView.withTime ? 'dd.MM.yyyy hh:mm' : 'dd.MM.yyyy';

              return format(value, dateFormat);
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors}>
                  <p className={styles.textEllipsis}>{params.formattedValue}</p>
                </Cell>
              );
            },
            renderEditCell: (params: GridRenderEditCellParams<DirectoryRecord>) => {
              const id = params.api.getRowId(params.row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const value = (() => {
                const _value = record.data[columnView.fieldId];

                if (!_value || typeof _value !== 'number') {
                  return null;
                }

                return _value;
              })();

              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              const dateFormat = columnView.withTime ? 'dd.MM.yyyy hh:mm' : 'dd.MM.yyyy';

              return (
                <Cell message={errors} style={{ padding: 0 }}>
                  <TableDatePicker
                    value={value}
                    cellParams={params}
                    dateFormat={dateFormat}
                    showTime={!!columnView.withTime}
                    autoFocus={params.hasFocus}
                    onChange={(value) => {
                      const preprocessedDate = (() => {
                        if (!hasValue(value) || !!columnView.withTime) {
                          return value;
                        }

                        return getUnixTime(startOfDay(fromUnixTime(value)));
                      })();

                      directory.directoryViewMediator?.onControlValueChange(
                        params.row.recordId,
                        columnView.fieldId,
                        preprocessedDate,
                      );
                    }}
                    onBlur={() => {
                      const record = directory.getRecordById(params.row.recordId);

                      if (!record) {
                        console.error('cannot find corresponding record');
                        return;
                      }

                      directory.onControlBlur(record, columnView.fieldId);
                    }}
                  />
                </Cell>
              );
            },
          });

          break;
        }

        case 'ComboBox': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'singleSelect',
            valueOptions() {
              const actualOptions = directory.optionsService.options[columnView.fieldId] ?? [];
              const archivedOptions = directory.optionsService.archivedOptions[columnView.fieldId] ?? [];

              return [...actualOptions, ...archivedOptions];
            },
            editable: directoryPage.isEditing,
            sortComparator: (v1, v2) => {
              const opt1 =
                directory.optionsService.optionsMap[columnView.fieldId].get(v1) ??
                directory.optionsService.archivedOptionsMap[columnView.fieldId].get(v1);
              const opt2 =
                directory.optionsService.optionsMap[columnView.fieldId].get(v2) ??
                directory.optionsService.archivedOptionsMap[columnView.fieldId].get(v2);

              const v1Label = (typeof opt1 === 'object' && opt1 !== null ? opt1.label : v1)?.toString() ?? '';
              const v2Label = (typeof opt2 === 'object' && opt2 !== null ? opt2.label : v2)?.toString() ?? '';

              return v1Label.localeCompare(v2Label);
            },
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const value = record.data[columnView.fieldId] ?? null;

              return value;
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors}>
                  <p className={styles.textEllipsis}>{params.formattedValue ?? params.value}</p>
                </Cell>
              );
            },
            renderEditCell: (params: GridRenderEditCellParams<DirectoryRecord>) => {
              const record = directory.getRecordById(params.id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const errors = record.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors} style={{ padding: 0 }}>
                  <EditComboboxCell
                    params={params}
                    record={record}
                    controlView={columnView}
                    optionsService={directory.optionsService}
                    onCreateNewRecord={() => createNewRelatedRecord(columnView.refObjectType ?? '')}
                    onChange={(value) =>
                      directory.directoryViewMediator?.onControlValueChange(
                        params.row.recordId,
                        columnView.fieldId,
                        value,
                      )
                    }
                    onBlur={() => {
                      const record = directory.getRecordById(params.row.recordId);

                      if (!record) {
                        console.error('cannot find corresponding record');
                        return;
                      }

                      directory.onControlBlur(record, columnView.fieldId);
                    }}
                  />
                </Cell>
              );
            },
          });

          break;
        }

        case 'MultiComboBox': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'string',
            editable: directoryPage.isEditing,
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const value = record.data[columnView.fieldId] ?? null;

              const labels: string[] = [];

              if (!value || !Array.isArray(value)) {
                return '-';
              }

              value.forEach((v) => {
                const correspondingOption =
                  directory.optionsService.optionsMap[columnView.fieldId]?.get(v) ??
                  directory.optionsService.archivedOptionsMap[columnView.fieldId]?.get(v);

                if (correspondingOption) {
                  labels.push(correspondingOption.label);
                }
              });

              return labels.join(', ') || '-';
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors}>
                  <p className={styles.textEllipsis}>{params.value}</p>
                </Cell>
              );
            },
            renderEditCell: (params: GridRenderEditCellParams<DirectoryRecord>) => {
              const record = directory.getRecordById(params.id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const errors = record.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors} style={{ padding: 0 }}>
                  <EditComboboxCell
                    params={params}
                    controlView={columnView}
                    record={record}
                    optionsService={directory.optionsService}
                    onCreateNewRecord={() => createNewRelatedRecord(columnView.refObjectType ?? '')}
                    onChange={(value) =>
                      directory.directoryViewMediator?.onControlValueChange(
                        params.row.recordId,
                        columnView.fieldId,
                        value,
                      )
                    }
                    onBlur={() => {
                      const record = directory.getRecordById(params.row.recordId);

                      if (!record) {
                        console.error('cannot find corresponding record');
                        return;
                      }

                      directory.onControlBlur(record, columnView.fieldId);
                    }}
                  />
                </Cell>
              );
            },
          });

          break;
        }

        case 'BitmapImage': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'string',
            width: 200,
            minWidth: 120,
            editable: directoryPage.isEditing,
            valueGetter: (_, row) => directory.getRecordById(row.recordId)?.data[columnView.fieldId] ?? null,
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const record = directory.getRecordById(params.id);
              if (!record) {
                return null;
              }

              return (
                <Cell style={{ padding: 0 }} message={record.fieldsErrors[columnView.fieldId]?.join(', ')}>
                  <BitmapImageComponent key="view" record={record} view={columnView} />
                </Cell>
              );
            },
            renderEditCell: (params: GridRenderEditCellParams<DirectoryRecord>) => {
              const record = directory.getRecordById(params.id);
              if (!record) {
                return null;
              }

              return (
                <Cell style={{ padding: 0 }}>
                  <BitmapImageComponent
                    key="edit"
                    record={record}
                    view={columnView}
                    isEditing
                    onChange={(value) =>
                      directory.directoryViewMediator?.onControlValueChange(record.recordId, columnView.fieldId, value)
                    }
                    onClose={() => {
                      directory.onControlBlur(record, columnView.fieldId);
                      if (params.api.getCellMode(params.id, params.field) === 'edit') {
                        params.api.stopCellEditMode({ id: params.id, field: params.field });
                      }
                      params.api.setCellFocus(params.id, params.field);
                    }}
                  />
                </Cell>
              );
            },
          });
          break;
        }

        case 'ColorPicker': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'string',
            editable: directoryPage.isEditing,
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              return record.data[columnView.fieldId] ?? null;
            },
            valueFormatter(value) {
              if (!hasValue(value) || typeof value !== 'string') {
                return '-';
              }

              return value;
            },
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;
              const value = typeof params.value === 'string' ? params.value : null;

              return (
                <Cell style={{ padding: 0 }} message={errors}>
                  <ColorPickerComponent value={value} />
                </Cell>
              );
            },
            renderEditCell: (params: GridRenderEditCellParams<DirectoryRecord>) => {
              const record = directory.getRecordById(params.row.recordId);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const value = record.data[columnView.fieldId];
              const errors = record.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell style={{ padding: 0 }} message={errors}>
                  <ColorPickerComponent
                    value={typeof value === 'string' ? value : null}
                    isEditing
                    cellParams={params}
                    onChange={(value) =>
                      directory.directoryViewMediator?.onControlValueChange(
                        params.row.recordId,
                        columnView.fieldId,
                        value,
                      )
                    }
                    onBlur={() => directory.onControlBlur(record, columnView.fieldId)}
                  />
                </Cell>
              );
            },
          });

          break;
        }

        case 'Label': {
          columns.push({
            ...baseColumn,
            field: columnView.fieldId,
            type: 'string',
            renderHeader: () => renderColumnHeader(columnView.fieldId),
            valueGetter: ($, row, _, api) => {
              const id = api.current.getRowId(row);
              const record = directory.getRecordById(id);

              if (!record) {
                console.error('cannot find corresponding record');
                return null;
              }

              const value = record.data[columnView.fieldId] ?? null;

              const isSimpleLabel = !('refObjectType' in columnView);

              if (hasValue(record.fieldRulesData[columnView.fieldId]?.displayValue)) {
                return record.fieldRulesData[columnView.fieldId]!.displayValue;
              }

              if (Array.isArray(value)) {
                return '-';
              }

              if (isSimpleLabel) {
                return value ?? '-';
              }

              if (!hasValue(value)) {
                return '-';
              }

              if (typeof value !== 'number') {
                console.error('control.value is not a number');
                return '-';
              }

              const option =
                directory.optionsService.optionsMap[columnView.fieldId].get(value) ??
                directory.optionsService.archivedOptionsMap[columnView.fieldId].get(value);

              return option?.label ?? '-';
            },
            renderCell: (params: GridRenderCellParams<DirectoryRecord>) => {
              const errors =
                directory.recordsMap.get(params.id)?.fieldsErrors[columnView.fieldId]?.join(', ') ?? undefined;

              return (
                <Cell message={errors}>
                  <p className={styles.textEllipsis}>{params.formattedValue}</p>
                </Cell>
              );
            },
          });
        }
      }
    }

    return columns;
  }, [controlViews, createNewRelatedRecord, directory, directoryPage.isEditing, rowNumberWidth]);
};
